// TaskQueuePanel shows the upcoming work in the order the team agreed on.
// Keeping it in its own file makes the main focus screen easy to scan.

import React from 'react';
import { format } from 'date-fns';
import { Clock, Calendar } from 'lucide-react';
import { TaskWithContext } from '../../lib/db';
import { formatMinutesShort, formatMinutesLong } from './focusUtils';

interface TaskQueuePanelProps {
  tasks: TaskWithContext[];
  currentTaskId?: string | null;
}

export const TaskQueuePanel: React.FC<TaskQueuePanelProps> = ({ tasks, currentTaskId }) => {
  if (!tasks.length || tasks.length <= 1) {
    return null; // No need to show the queue if there's only the current task.
  }

  const totalMinutes = tasks.reduce((sum, task) => sum + (task.time_estimate || 0), 0);
  const hasAnyEstimates = tasks.some(task => typeof task.time_estimate === 'number' && task.time_estimate > 0);

  return (
    <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Queue</h2>
      <div className="space-y-3">
        {tasks.map((task, index) => {
          const isCurrentTask = task.id === currentTaskId;
          const isOverdue = !!task.isOverdue;
          const displayDeadline = task.queueDueDate;
          const hasUsableEstimate = typeof task.time_estimate === 'number' && task.time_estimate > 0;
          const estimateLabel = hasUsableEstimate
            ? formatMinutesShort(task.time_estimate!)
            : 'No estimate yet';

          return (
            <div
              key={task.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                isCurrentTask ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                  <span className={`font-medium ${isCurrentTask ? 'text-blue-900' : 'text-gray-900'}`}>
                    {task.title}
                  </span>
                  {isOverdue && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                      Overdue
                    </span>
                  )}
                  {task.queueCategory === 'regular' && !isOverdue && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                      Regular
                    </span>
                  )}
                  {task.queueCategory === 'deliverable' && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      Deliverable
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    task.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                    task.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {task.priority}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {estimateLabel}
                  </span>
                  {displayDeadline && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(displayDeadline, 'MMM d')}
                    </span>
                  )}
                </div>
              </div>
              {isCurrentTask && <span className="text-sm font-medium text-blue-600">Current</span>}
            </div>
          );
        })}
      </div>

      {hasAnyEstimates && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Total estimated time:</span>
            <span className="font-medium text-gray-900">{formatMinutesLong(totalMinutes)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskQueuePanel;
